/**
 * citation-chip domain entities — the Word-document-specific layer
 * on top of the (FE-shared) citation domain.
 *
 * A Chip is a single in-document occurrence of a citation, anchored
 * by a ContentControl (Office.js binding). The CitationChip carries
 * its own identity (`cid`) because the same `refKey` can appear in
 * multiple paragraphs of the same brief — each occurrence needs to
 * round-trip independently through reverify / drift reconciliation.
 *
 * Pure types — no Office.js, no React, no fetch. The seam to the
 * binding layer lives in `./ports.ts` (ChipStoragePort,
 * ChipResolverPort).
 */

/** Citation kind — driven from the refKey shape at tag-encode time
 *  (S1 PATCH from plan §2.2). NOT a BE wire field. Drives chip
 *  color/badge choice:
 *
 *  - `article` → blue (Code civil, etc.)
 *  - `jp`      → gold (jurisprudence ECLI)
 *  - `piece`   → violet (PR-4+)
 *  - `fait`    → green (PR-4+)
 *
 *  PR-3a scope: `article` + `jp` only. `piece` + `fait` land with
 *  Sources tab + Faits tab consumer PRs. */
export type ChipKind = 'article' | 'jp' | 'piece' | 'fait'

/** Verification status (D2 LOCKED 2026-05-25, 5-value enum):
 *
 *  - `pending`        → chip inserted, BE never consulted (score is
 *                       meaningless — hover popover suppresses it)
 *  - `verified`       → BE returned `score >= 0.85` on last reverify
 *  - `unverified`     → BE returned a lower score, doc could be wrong
 *  - `stale`          → last reverified > N hours ago (registry may
 *                       have changed)
 *  - `broken-anchor`  → tag malformed or BE no longer recognises the
 *                       refKey */
export type ChipStatus =
  | 'pending'
  | 'verified'
  | 'unverified'
  | 'stale'
  | 'broken-anchor'

/** Drafting language for the chip. Mirrors `brief.language` from the
 *  XmlPart anchor (the lawyer's chosen brief language, NOT the Office
 *  UI language). Encoded in the tag so chip rendering picks the right
 *  citation template without an XmlPart round-trip. */
export type ChipLanguage = 'fr' | 'ar' | 'en'

/** Canonical CitationChip — the value object the rest of the add-in
 *  reasons about. Office binding details (CC id, body text) live in
 *  the adapter; this type is the domain-level handle.
 *
 *  Note: `cid` is the primary key. Two chips can share `refKey`
 *  (same article cited in 3 paragraphs) and they will each carry
 *  a distinct `cid`. */
export interface CitationChip {
  cid: string
  kind: ChipKind
  refKey: string
  status: ChipStatus
  /** Confidence score from BE registry (0.00–1.00, 2-decimal precision).
   *  `0.00` is the sentinel for `pending` chips. */
  score: number
  language: ChipLanguage
  /** ISO 8601 UTC timestamp ("Z" suffix mandatory). */
  insertedAt: string
}

/** Decoded payload of a ContentControl tag — produced by
 *  `tag-codec.decode()` (lives in the adapter). The domain consumes
 *  this shape to construct a CitationChip but never produces the
 *  encoded string itself. */
export interface ChipTag {
  cid: string
  kind: ChipKind
  refKey: string
  status: ChipStatus
  score: number
  language: ChipLanguage
  insertedAt: string
}

/** Derive the ChipKind from the refKey shape (S1 PATCH from plan §2.2).
 *
 *  - `ecli:...` → `jp` (jurisprudence)
 *  - `art<n>|...|...` → `article`
 *  - `piece:...` → `piece` (PR-4+)
 *  - `fait:...` → `fait`  (PR-4+)
 *
 *  Throws `Error` for shapes not matching any kind — callers MUST
 *  guard upstream. */
export function deriveChipKind(refKey: string): ChipKind {
  if (refKey.startsWith('ecli:')) return 'jp'
  if (/^art[^|]+\|/.test(refKey)) return 'article'
  if (refKey.startsWith('piece:')) return 'piece'
  if (refKey.startsWith('fait:')) return 'fait'
  throw new Error(`unknown refKey shape: ${refKey.slice(0, 30)}`)
}
